import { Button, message, Popconfirm, Typography, Form } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import BasicLayout from "../../../components/BasicLayout/index";
import ButtonAsLink from "../../../components/common/ButtonAsLink";
import FallBack from "../../../components/common/FallBack/FallBack";
import { useDeleteAction } from "../../../components/Hooks/useDeleteAction";
import useExportCsv from "../../../components/Hooks/useExportCsv";
import useTableSearch from "../../../components/Hooks/useTableSearch";
import {
	getAllImplements,
	softDeleteSingleImplements,
	updateImplementFromTable,
} from "../../../store/actions/products/implements/implements.action";
import { DeleteModel } from "../../../utils/DeleteModel";
import { formatFileName } from "../../../utils/helper";
import { useUpdateReason } from "../../../components/Hooks/useUpdateReason";
import EditableTable from "../../../components/common/Table/EditableTable";

const Implement = (props) => { 
	const [getColumnSearchProps] = useTableSearch();
	const [exportToCSV] = useExportCsv();
	const { deleteData } = useDeleteAction();
	const [deleteId, setDeleteId] = useState(undefined);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [onSaveValues, setOnSaveValues] = useState();
	const [updateImplementId, setUpdateImplementId] = useState();
	const [data, setData] = useState([]);
	const [form] = Form.useForm();
	const { modelToggle, UpdateModel } = useUpdateReason();

	const [editingKey, setEditingKey] = useState("");
	const isEditing = (record) => record.key === editingKey;

	const dispatch = useDispatch();
	const multipleImplements = useSelector((state) => state.implements.multipleImplements || []);

	const admin = useSelector((state) => state.admin.admin || {});
	const permissionArr = useMemo(() => {
		if (admin && admin.template) {
			const { permissions } = admin.template;
			return permissions;
		} else if (admin && admin.accessLevel === "superAdmin") {
			return "all";
		}
		return [];
	}, [admin]);

	useEffect(() => {
		dispatch(getAllImplements());
	}, [dispatch]);

	function originDataUpdate(accepetedData) {
		let tempdata = [];
		for (let i = 0; i < accepetedData.length; i++) {
			tempdata.push({
				id: accepetedData[i].id,
				key: accepetedData[i].key,
				name: accepetedData[i].name,
				code: accepetedData[i].code,
				stock: accepetedData[i].stock,
				price: accepetedData[i].price,
				discount: accepetedData[i].discount,
				hsnCode: accepetedData[i]?.hsnCode,
			});
		}

		return tempdata;
	}

	function dataSourceUpdate(accepetedData) {
		let tempDataSource = [];
		if (accepetedData.length > 0) {
			tempDataSource = accepetedData.sort(function (a, b) {
				return new Date(b.updatedAt) - new Date(a.updatedAt);
			});
			tempDataSource = tempDataSource.map((row) => ({
				...row,
				createdAt: new Date(row.createdAt).toLocaleString(),
				key: row.id,
				hsnCode: row?.hsnCode,
			}));
		}
		return tempDataSource;
	}

	const dataSource = useMemo(() => {
		return dataSourceUpdate(multipleImplements);
	}, [multipleImplements]);

	const originData = useMemo(() => {
		return originDataUpdate(dataSource);
	}, [dataSource]);

	async function confirm(id, reason) {
		await deleteData(id, reason, softDeleteSingleImplements, setIsModalVisible);
	}

	useEffect(() => {
		setData(originData);
	}, [originData]);

	const save = async (key) => {
		try {
			setUpdateImplementId(key);
			const row = await form.validateFields();
			const newData = [...data];
			const index = newData.findIndex((item) => key === item.key);
			if (index > -1) {
				const item = newData[index];
				newData.splice(index, 1, { ...item, ...row });
				let { discount, stock, price } = newData[index];
				let data = {
					discount,
					stock,
					price,
				};
				setOnSaveValues(data);
				modelToggle(true);
				setEditingKey("");
			} else {
				setEditingKey("");
			}
		} catch (errInfo) {
			return message.error(`Something went wrong`);
		}
	};

	const columns = [
		{
			title: "Id",
			dataIndex: "id",
			key: "id",
		},
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			...getColumnSearchProps("name"),
		},
		{
			title: "Code",
			dataIndex: "code",
			key: "code",
			...getColumnSearchProps("code"),
		},
		{
			title: "Stock",
			dataIndex: "stock",
			key: "stock",
			editable: true,
			...getColumnSearchProps("stock"),
		},
		{
			title: "Price",
			dataIndex: "price",
			key: "price",
			min: 1,
			editable: true,
			...getColumnSearchProps("price"),
		},
		{
			title: "Discount",
			dataIndex: "discount",
			key: "discount",
			min: 0,
			max: 100,
			editable: true,
			...getColumnSearchProps("discount"),
		},
		{
			title: "HSN/SAC Code",
			key: "hsnCode",
			...getColumnSearchProps("hsnCode"),
			render: (_text, record) => (
				<a href={`/products/hsncode/${record?.hsnCode?.id}`}> {record?.hsnCode?.code}</a>
			),
		},
		{
			title: "Action",
			key: "action",
			render: (_text, record) => (
				<DeleteModel
					record={record}
					confirm={confirm}
					setIsModalVisible={setIsModalVisible}
					isModalVisible={isModalVisible}
					cancelMessage="Implements Removing has been cancelled"
					linkUrl={`/products/implements`}
					placeholder="Please enter reason to delete implements"
					deleteId={deleteId}
					setDeleteId={setDeleteId}
					update={
						permissionArr === "all"
							? "true"
							: permissionArr?.products?.implements.filter((item) => item === "update")?.length > 0
							? "true"
							: false
					}
					isdelete={
						permissionArr === "all"
							? "true"
							: permissionArr?.products?.implements.filter((item) => item === "delete")?.length > 0
							? "true"
							: false
					}
					view={
						permissionArr === "all"
							? "true"
							: permissionArr?.products?.implements.filter((item) => item === "read")?.length > 0
							? "true"
							: false
					}
				/>
			),
		},
		{
			title: "operation",
			dataIndex: "operation",
			render: (_, record) => {
				const editable = isEditing(record);
				return editable ? (
					<div style={{ width: "100%", display: "flex" }}>
						<span
							onClick={() => save(record.key)}
							style={{
								display: "inline-block",
								marginRight: "10px",
								color: "#1890ff",
								cursor: "pointer",
							}}
						>
							Save
						</span>
						<Popconfirm title="Sure to cancel?" onConfirm={() => setEditingKey("")}>
							<span style={{ color: "#1890ff", cursor: "pointer" }}>Cancel</span>
						</Popconfirm>
					</div>
				) : (
					<Typography.Link disabled={editingKey !== ""} onClick={() => edit(record)}>
						Edit
					</Typography.Link>
				);
			},
		},
	];

	const edit = (record) => {
		form.setFieldsValue({
			...record,
		});
		setEditingKey(record.key);
	};

	const fileName = formatFileName("implements", "xlsx", admin);
	let downloadData = multipleImplements.map((data) => {
		let newData = {
			"Implement Name": data.name,
			"Implement Code": data.code,
			"HSC CODE": data?.hsnCode?.code,
			"HSN CODE ID": data?.hsnCode?.id,
			STOCK: data?.stock,
			PRICE: data?.price,
			DISCOUNT: data?.discount,
			createdAt: data.createdAt,
		};
		return newData;
	});
	const handleError = (error) => {
		message.error(error.message);
	};
	return (
		<BasicLayout accessLevel={props.accessLevel}>
			<ErrorBoundary FallbackComponent={FallBack} onError={handleError}>
				<div className="button-style-to-right">
					{permissionArr === "all" ? (
						<Button className="download-btn" onClick={(e) => exportToCSV(downloadData, fileName)}>
							Export
						</Button>
					) : permissionArr?.products?.implements?.filter((item) => item === "export")?.length >
							0 && downloadData?.length !== 0 ? (
						<Button className="download-btn" onClick={(e) => exportToCSV(downloadData, fileName)}>
							Export
						</Button>
					) : null}
					{permissionArr === "all" ? (
						<ButtonAsLink text="Add New Implement" link="/products/implements/add" />
					) : permissionArr?.products?.implements?.filter((item) => item === "write")?.length >
					  0 ? (
						<ButtonAsLink text="Add New Implement" link="/products/implements/add" />
					) : null}
				</div>
				<EditableTable
					form={form}
					columns={columns}
					data={data}
					isEditing={isEditing}
					footerText={`Total Models: ${multipleImplements?.length}`}
				/>
				<UpdateModel
					id={updateImplementId}
					action={updateImplementFromTable}
					valuesWithoutReason={onSaveValues}
					redirectUrl="/products/implements"
					placeholder="Enter reason for update implement"
				/>
			</ErrorBoundary>
		</BasicLayout>
	);
};

export default Implement;
